<template>
    <RegularModal ref="modal">
        <template v-slot:title>{{ translate("admin_results.change_individual_results_options") }}</template>
        <template v-slot:body>
            <p class="has-text-weight-bold mb-1">{{ translate("admin_results.general_options") }}</p>

            <div>
                <Checkbox2 v-model="showFullClassification">
                    {{ translate("admin_results.show_full_list_results") }}
                </Checkbox2>
            </div>

            <p class="has-text-weight-bold mt-3 mb-1">{{ translate("bracket_type.round_robin") }}:</p>

            <div>
                <Checkbox2 v-model="roundRobinWithTwo3rdPlaces">
                    {{ translate("admin_results.round_robin_with_two_3rd") }}
                </Checkbox2>
            </div>

            <p class="has-text-weight-bold mt-3 mb-1">{{ translate("admin_results.double_elimination") }}:</p>

            <div>
                <Checkbox2 v-model="doubleEliminationWrestlingRules" @change="onDoubleEliminationWrestlingRulesChange">
                    {{ translate("admin_results.double_elimination_accordingly_to_wrestling") }}
                </Checkbox2>

                <a target="_blank" :href="urls.helpCenter().wrestlingClassification()">
                    <span class="icon">
                        <i class="fa fa-question-circle" />
                    </span>
                </a>
            </div>

            <div>
                <Radio
                    name="doubleElimination5thPlaceCalculation"
                    v-model="doubleElimination5thPlaceCalculation"
                    :initial-value="FifthPlaceCalculationEnum.Two5thPlaces"
                    :disabled="!doubleEliminationWrestlingRules"
                >
                    {{ translate("admin_results.doubleElimination5thPlaceCalculation.two5thPlaces") }}
                </Radio>
                <Radio
                    name="doubleElimination5thPlaceCalculation"
                    v-model="doubleElimination5thPlaceCalculation"
                    :initial-value="FifthPlaceCalculationEnum.One5thOne6thPlace"
                    :disabled="!doubleEliminationWrestlingRules"
                >
                    {{ translate("admin_results.doubleElimination5thPlaceCalculation.one5thOne6thPlace") }}
                </Radio>
            </div>

            <p class="has-text-weight-bold mt-3 mb-1">{{ translate("admin_results.two_pools_bracket") }}:</p>

            <div>
                <Checkbox2 v-model="twoPoolsWrestlingRules">
                    {{ translate("admin_results.two_pools_accordingly_to_wrestling") }}
                </Checkbox2>
            </div>

            <div>
                <Radio
                    name="twoPools5thPlaceCalculation"
                    v-model="twoPools5thPlaceCalculation"
                    :initial-value="FifthPlaceCalculationEnum.Two5thPlaces"
                    :disabled="!twoPoolsWrestlingRules"
                >
                    {{ translate("admin_results.doubleElimination5thPlaceCalculation.two5thPlaces") }}
                </Radio>
                <Radio
                    name="twoPools5thPlaceCalculation"
                    v-model="twoPools5thPlaceCalculation"
                    :initial-value="FifthPlaceCalculationEnum.One5thOne6thPlace"
                    :disabled="!twoPoolsWrestlingRules"
                >
                    {{ translate("admin_results.doubleElimination5thPlaceCalculation.one5thOne6thPlace") }}
                </Radio>
            </div>

            <p class="has-text-weight-bold mt-3 mb-1">{{ translate("bracket_type.repechage_by_finalists") }}:</p>

            <div>
                <Checkbox2 v-model="repechageByFinalistsWrestlingRules" @change="onRepechageByFinalistsWrestlingRulesChange">
                    {{ translate("admin_results.repechage_by_finalists_accordingly_to_wrestling") }}
                </Checkbox2>
            </div>

            <div>
                <Radio
                    name="repechageByFinalists5thPlaceCalculation"
                    v-model="repechageByFinalists5thPlaceCalculation"
                    :initial-value="FifthPlaceCalculationEnum.Two5thPlaces"
                    :disabled="!repechageByFinalistsWrestlingRules"
                >
                    {{ translate("admin_results.doubleElimination5thPlaceCalculation.two5thPlaces") }}
                </Radio>
                <Radio
                    name="repechageByFinalists5thPlaceCalculation"
                    v-model="repechageByFinalists5thPlaceCalculation"
                    :initial-value="FifthPlaceCalculationEnum.One5thOne6thPlace"
                    :disabled="!repechageByFinalistsWrestlingRules"
                >
                    {{ translate("admin_results.doubleElimination5thPlaceCalculation.one5thOne6thPlace") }}
                </Radio>
            </div>

            <p class="has-text-weight-bold mt-3 mb-1">{{ translate("common.sort") }}:</p>

            <div class="control is-flex is-align-items-center mb-3">
                <div class="select">
                    <select v-model="resultsSorting">
                        <option value="endTime">
                            {{ translate("admin_results.sort_by_category_end_time") }}
                        </option>
                        <option value="categoriesOrder">
                            {{ translate("admin_results.sort_by_category_order") }}
                        </option>
                    </select>
                </div>
            </div>
        </template>
        <template v-slot:footer>
            <div class="buttons">
                <button class="button" @click="close">{{ translate("common.cancel") }}</button>

                <LoadingButton :is-loading="savingOptionsInProgress" class="button is-primary" @click="savePublicResultsOptions">
                    {{ translate("common.save") }}
                </LoadingButton>
            </div>
        </template>
    </RegularModal>
</template>

<script setup lang="ts">
    import RegularModal from "../../Common/RegularModal.vue";
    import { useModal } from "../../Common/useModal";
    import urls from "../../Urls/urls";
    import Checkbox2 from "../../Common/Checkbox2.vue";
    import { ref } from "vue";
    import { translate } from "../../Common/translations";
    import {
        FifthPlaceCalculation,
        FifthPlaceCalculationEnum,
        ResultsReportOptions,
        ResultsSorting,
        ResultsSortingEnum,
    } from "../../Api/competitorResultsApi";
    import LoadingButton from "../../Common/LoadingButton.vue";
    import { successToast } from "../../Common/toasts";
    import { handleError } from "../../Common/errorHandler";
    import { useCompetitorResultsOptionsStore } from "./competitorResultsOptionsStore";
    import Radio from "../../Common/Radio.vue";

    const props = defineProps<{
        eventId: number;
    }>();

    const store = useCompetitorResultsOptionsStore();

    const showFullClassification = ref(false);
    const roundRobinWithTwo3rdPlaces = ref(false);
    const doubleEliminationWrestlingRules = ref(false);
    const doubleElimination5thPlaceCalculation = ref<FifthPlaceCalculation | undefined>(FifthPlaceCalculationEnum.Two5thPlaces);
    const twoPoolsWrestlingRules = ref(false);
    const twoPools5thPlaceCalculation = ref<FifthPlaceCalculation | undefined>(FifthPlaceCalculationEnum.Two5thPlaces);
    const repechageByFinalistsWrestlingRules = ref(false);
    const repechageByFinalists5thPlaceCalculation = ref<FifthPlaceCalculation | undefined>(FifthPlaceCalculationEnum.Two5thPlaces);
    const resultsSorting = ref<ResultsSorting>(ResultsSortingEnum.EndTime);

    const { modal, open: openModal, close } = useModal();

    function open(currentOptions: ResultsReportOptions) {
        showFullClassification.value = currentOptions.fullList;
        roundRobinWithTwo3rdPlaces.value = currentOptions.roundRobinWithTwo3rdPlaces;
        doubleEliminationWrestlingRules.value = currentOptions.doubleEliminationWrestlingRules;
        doubleElimination5thPlaceCalculation.value = currentOptions.doubleElimination5thPlaceCalculation;
        twoPoolsWrestlingRules.value = currentOptions.twoPoolsWrestlingRules;
        twoPools5thPlaceCalculation.value = currentOptions.twoPools5thPlaceCalculation;
        repechageByFinalistsWrestlingRules.value = currentOptions.repechageByFinalistsWrestlingRules;
        repechageByFinalists5thPlaceCalculation.value = currentOptions.repechageByFinalists5thPlaceCalculation;
        resultsSorting.value = currentOptions.resultsSorting;

        openModal();
    }

    function onDoubleEliminationWrestlingRulesChange() {
        if (doubleEliminationWrestlingRules.value) {
            doubleElimination5thPlaceCalculation.value = FifthPlaceCalculationEnum.Two5thPlaces;
        } else {
            doubleElimination5thPlaceCalculation.value = undefined;
        }
    }

    function onRepechageByFinalistsWrestlingRulesChange() {
        if (repechageByFinalistsWrestlingRules.value) {
            repechageByFinalists5thPlaceCalculation.value = FifthPlaceCalculationEnum.Two5thPlaces;
        } else {
            repechageByFinalists5thPlaceCalculation.value = undefined;
        }
    }

    const emit = defineEmits(["changed"]);

    const savingOptionsInProgress = ref(false);

    function savePublicResultsOptions(): Promise<void> {
        savingOptionsInProgress.value = true;

        const de5thPlaceCalc = doubleEliminationWrestlingRules.value ? doubleElimination5thPlaceCalculation.value : undefined;
        const twoPools5thPlaceCalc = twoPoolsWrestlingRules.value ? twoPools5thPlaceCalculation.value : undefined;
        const repechageByFinalists5thPlaceCalc = repechageByFinalistsWrestlingRules.value
            ? repechageByFinalists5thPlaceCalculation.value
            : undefined;

        return store
            .saveResultsReportOptions(props.eventId, {
                fullList: showFullClassification.value,
                roundRobinWithTwo3rdPlaces: roundRobinWithTwo3rdPlaces.value,
                doubleEliminationWrestlingRules: doubleEliminationWrestlingRules.value,
                doubleElimination5thPlaceCalculation: de5thPlaceCalc,
                twoPoolsWrestlingRules: twoPoolsWrestlingRules.value,
                twoPools5thPlaceCalculation: twoPools5thPlaceCalc,
                repechageByFinalistsWrestlingRules: repechageByFinalistsWrestlingRules.value,
                repechageByFinalists5thPlaceCalculation: repechageByFinalists5thPlaceCalc,
                resultsSorting: resultsSorting.value,
            })
            .then(() => {
                store.resultOptions = {
                    fullList: showFullClassification.value,
                    roundRobinWithTwo3rdPlaces: roundRobinWithTwo3rdPlaces.value,
                    doubleEliminationWrestlingRules: doubleEliminationWrestlingRules.value,
                    doubleElimination5thPlaceCalculation: doubleElimination5thPlaceCalculation.value,
                    twoPoolsWrestlingRules: twoPoolsWrestlingRules.value,
                    twoPools5thPlaceCalculation: twoPools5thPlaceCalculation.value,
                    repechageByFinalistsWrestlingRules: repechageByFinalistsWrestlingRules.value,
                    repechageByFinalists5thPlaceCalculation: repechageByFinalists5thPlaceCalculation.value,
                    resultsSorting: resultsSorting.value,
                };
                successToast(translate("admin_results.sharing_options_updated"));
                emit("changed");
                close();
            })
            .catch(handleError)
            .finally(() => {
                savingOptionsInProgress.value = false;
            });
    }

    defineExpose({ open });
</script>

<style scoped></style>
